/**
 * <e-primitive> — vanilla custom element. Renders as any HTML element via `as`.
 *
 * Light-DOM only (no shadow root). Tailwind / scoped styles in the parent shadow root
 * apply directly to the inner element. The host has `display: contents` via the project's
 * global CSS so it adds no layout box.
 *
 * Component-generated classes pass via `inner-class` (not native `class`) so they never
 * trigger CSS matchers on the custom element host. Consumer-provided `class` is also
 * forwarded to the inner element.
 *
 * A MutationObserver forwards all non-reserved attribute changes to the inner element so
 * reactive bindings (`:disabled`, `:style`, `:aria-*`, `:data-*`, etc.) stay in sync.
 */

const RESERVED_ATTRS = new Set(["as", "inner-class", "class"]);

export class EPrimitive extends HTMLElement {
  static readonly observedAttributes = ["as", "inner-class", "class"];

  private innerEl: HTMLElement | null = null;
  private currentAs = "";
  private observer: MutationObserver | null = null;

  connectedCallback() {
    if (!this.innerEl) this.upgrade();
    this.applyAttrs();
    this.startObserving();
  }

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null) {
    if (oldValue === newValue) return;
    if (!this.innerEl) return;
    if (name === "as") {
      this.upgrade();
    } else if (name === "inner-class" || name === "class") {
      this.applyClasses();
    }
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.observer = null;
  }

  private startObserving() {
    if (this.observer) return;
    this.observer = new MutationObserver((mutations) => {
      let asChanged = false;
      let classOrInnerChanged = false;
      let otherAttr = false;
      for (const m of mutations) {
        if (m.type !== "attributes" || !m.attributeName) continue;
        if (m.attributeName === "as") asChanged = true;
        else if (m.attributeName === "class" || m.attributeName === "inner-class") classOrInnerChanged = true;
        else otherAttr = true;
      }
      if (asChanged) this.upgrade();
      if (classOrInnerChanged) this.applyClasses();
      if (otherAttr) this.applyAttrs();
    });
    this.observer.observe(this, { attributes: true });
  }

  private upgrade() {
    const as = this.getAttribute("as") ?? "div";
    if (this.innerEl && this.currentAs === as) return;
    this.currentAs = as;

    const newInner = document.createElement(as);
    newInner.setAttribute("part", "base");

    const oldInner = this.innerEl;
    const source = oldInner ?? this;
    while (source.firstChild) {
      newInner.append(source.firstChild);
    }

    if (oldInner) {
      this.replaceChild(newInner, oldInner);
    } else {
      this.append(newInner);
    }
    this.innerEl = newInner;
    this.applyAttrs();
  }

  private applyAttrs() {
    const inner = this.innerEl;
    if (!inner) return;
    this.applyClasses();
    // Forward all non-reserved attributes
    const seen = new Set<string>();
    for (const attr of this.attributes) {
      if (RESERVED_ATTRS.has(attr.name)) continue;
      seen.add(attr.name);
      inner.setAttribute(attr.name, attr.value);
    }
    // Remove attributes from inner that no longer exist on host (excluding part="base").
    // Snapshot via spread because the loop calls `removeAttribute` and mutates the live NamedNodeMap.
    // eslint-disable-next-line unicorn/no-useless-spread -- snapshot required: mutation during iteration
    for (const attr of [...inner.attributes]) {
      if (RESERVED_ATTRS.has(attr.name)) continue;
      if (attr.name === "part") continue;
      if (attr.name === "class") continue;
      if (!seen.has(attr.name)) inner.removeAttribute(attr.name);
    }
  }

  private applyClasses() {
    const inner = this.innerEl;
    if (!inner) return;
    const innerClass = this.getAttribute("inner-class") ?? "";
    const klass = this.getAttribute("class") ?? "";
    const merged = `${innerClass} ${klass}`.trim();
    if (merged) {
      inner.className = merged;
    } else {
      inner.removeAttribute("class");
    }
  }
}
